import { createColumnHelper } from "@tanstack/react-table";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon, Input, Modal } from "semantic-ui-react";

import { ReactTable } from "components";
import "modules/DealerDetails/components/Invoicing/components/ExactSearch/ExactSearch.scss";
import { useLinkedExactAccountsQuery, useSearchExactAccountsMutation } from "modules/DealerDetails/components/Invoicing/components/ExactSearch/hooks";

import type { DealerID } from "models";
import type { ExactAccount } from "modules/DealerDetails/components/Invoicing/hooks";

const columnHelper = createColumnHelper<ExactAccount>();

type ExactSearchProps = {
  dealerId: DealerID;
  onSelectAccount: (accountId: string) => void;
};

export const ExactSearch = ({ dealerId, onSelectAccount }: ExactSearchProps) => {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [term, setTerm] = useState("");

  const { data: linkedAccounts } = useLinkedExactAccountsQuery(isOpen);
  const { mutate: searchAccounts, data: accounts, isPending: isSearchPending, reset: resetSearch } = useSearchExactAccountsMutation();

  const handleClose = () => {
    setIsOpen(false);
    setTerm("");
    resetSearch();
  };

  const handleLinkAccount = (accountId: string) => {
    onSelectAccount(accountId);
    handleClose();
  };

  const handleSearch = () => {
    if (isSearchPending || !term.trim()) return;

    searchAccounts({ dealer_id: dealerId, search: term.trim() });
  };

  const columns = [
    columnHelper.accessor("Code", {
      id: "code",
      size: 120,
      header: () => <span className="no-wrap-text">{t("v8_code", "Code")}</span>,
      cell: ({ getValue }) => <span className="no-wrap-text">{getValue()}</span>
    }),
    columnHelper.accessor("Name", {
      id: "company",
      size: 220,
      header: () => <span className="no-wrap-text">{t("v8_company_name", "Company name")}</span>,
      cell: ({ getValue }) => <span className="no-wrap-text">{getValue()}</span>
    }),
    columnHelper.accessor("City", {
      id: "city",
      size: 150,
      header: () => <span className="no-wrap-text">{t("v8_city", "City")}</span>,
      cell: ({ getValue }) => <span className="no-wrap-text">{getValue()}</span>
    }),
    columnHelper.accessor("AddressLine1", {
      id: "address",
      size: 200,
      header: () => <span className="no-wrap-text">{t("v8_address", "Address")}</span>,
      cell: ({ getValue }) => <span className="no-wrap-text">{getValue()}</span>
    }),
    columnHelper.accessor(account => account.Contact?.Email ?? "", {
      id: "email",
      size: 200,
      header: () => <span className="no-wrap-text">{t("v8_email", "E-mail")}</span>,
      cell: ({ getValue }) => <span className="no-wrap-text">{getValue()}</span>
    }),
    columnHelper.accessor(account => linkedAccounts?.get(account.ID) ?? "", {
      id: "linkedTo",
      size: 180,
      header: () => <span className="no-wrap-text">{t("v8_linked_to", "Linked to")}</span>,
      cell: ({ getValue }) => {
        const linkedTo = getValue();
        if (!linkedTo) return null;

        return (
          <span className="exact-search-linked-to" title={linkedTo}>
            {linkedTo}
          </span>
        );
      }
    }),
    columnHelper.accessor(account => account, {
      id: "actions",
      size: 80,
      header: "",
      cell: ({ row: { original: account } }) => {
        const linkedTo = linkedAccounts?.get(account.ID);

        return (
          <div className="exact-search-action-cell">
            <Button
              basic
              icon
              disabled={!!linkedTo}
              title={linkedTo ? t("v8_account_already_linked_to", "This account is already linked to {{linkedTo}}", { linkedTo }) : t("v8_link_account", "Link account")}
              onClick={() => handleLinkAccount(account.ID)}
            >
              <Icon className="linkify grey" />
            </Button>
          </div>
        );
      }
    })
  ];

  return (
    <>
      <Button type="button" icon color="green" title={t("v8_search_account", "Search account")} onClick={() => setIsOpen(true)}>
        <Icon className="magnifying glass" />
      </Button>

      <Modal open={isOpen} size="large" closeOnDimmerClick={false} onClose={handleClose} className="ExactSearchModal">
        <Modal.Header>
          <div>{t("v8_search_account", "Search account")}</div>
          <Button className="modal-close-btn" basic onClick={handleClose}>
            <Icon className="xmark" />
          </Button>
        </Modal.Header>

        <Modal.Content scrolling>
          <Input
            fluid
            value={term}
            placeholder={t("v8_search_accounts_by_name_company_or_relation_id", "Search accounts by name, company or relation ID")}
            onChange={(_, { value }) => setTerm(value)}
            onKeyDown={({ key }: React.KeyboardEvent) => key === "Enter" && handleSearch()}
            action={
              <Button color="green" loading={isSearchPending} disabled={isSearchPending || !term.trim()} onClick={handleSearch}>
                {t("v8_search", "Search")}
              </Button>
            }
          />

          {!!accounts && (
            <div className="exact-search-results">
              <ReactTable columns={columns} data={accounts} isFetching={isSearchPending} emptyRowMessage={t("v8_no_accounts_found", "No accounts found")} />
            </div>
          )}
        </Modal.Content>
      </Modal>
    </>
  );
};
